/**
 * Attach Calibration — embed a saved calibration's worksheet(s) INTO the current
 * QC-batch workbook. A faithful port of OG
 * `SpreadsheetEntryController.Dc_AcceptingAttachCalibation` (BTLIMS :10519).
 *
 * OG loads the chosen calibration BATCH's saved workbook and copies its sheet(s) into
 * the QC batch the analyst is entering, then flags `ISCalibAttach` + `strCB`:
 *   - template Model == CalibrationSet (2): copy EVERY worksheet except "Sequence" and
 *     "Summary" (the batch keeps its own of those) — i.e. the Level1..LevelN sheets
 *     (+ ISTD / Regression Curves).
 *   - otherwise: copy just the single "Calibration" worksheet, made visible.
 *
 * AI difference (the "reopen = re-merge, not blob" rule): the calibration is re-merged
 * from its saved CalibrationInfo rows (via `mergeSdmsTemplate`) into `calibBuf` by the
 * caller, not read from a stored blob; this module then copies the resulting sheet(s)
 * into the current batch workbook. The copied sheets ride along in the batch's saved
 * blob on the next Save (so the workbook OG later opens contains them — the coexistence
 * requirement that made the create-time Calibration FK unsafe until now).
 */

import { copySheet } from './sdmsMailMerge';
import { loadSdmsWorkbook, writeSdmsWorkbook } from './sdmsWorkbookIo';

/** OG CalibrationSet copies all sheets EXCEPT these (the batch owns its own). */
const SKIP_FOR_SET = new Set(['SEQUENCE', 'SUMMARY']);

/**
 * Embed a saved calibration's worksheet(s) into the current QC-batch workbook.
 *
 * @param currentBuf  the batch's LIVE workbook (from `getEditedXlsx`) — edits preserved.
 * @param calibBuf    the chosen calibration re-merged into a workbook (mergeSdmsTemplate).
 * @param model       the calibration template's Model (2 = CalibrationSet).
 * @returns the combined workbook buffer + the names of the sheets copied in.
 *
 * Model 2: a source sheet whose name already exists in the batch is SKIPPED (OG's
 * Worksheets.Add would fail on it). Otherwise the calibration's "Calibration" sheet REPLACES the
 * batch's own one in place, as OG's CopyFrom does.
 * CF `text=` attrs are re-repaired on the combined output so the blob still opens in OG
 * (see sdmsCfTextRepair.ts / the AI-blob-opens-in-OG note).
 */
export async function embedCalibrationSheets(
  currentBuf: ArrayBuffer,
  calibBuf: ArrayBuffer,
  model: number,
): Promise<{ bytes: ArrayBuffer; copied: string[] }> {
  const cur = await loadSdmsWorkbook(currentBuf);
  const copied = embedCalibrationSheetsIntoWorkbook(cur, await loadSdmsWorkbook(calibBuf), model);
  const bytes = await writeSdmsWorkbook(cur);
  return { bytes, copied };
}

/** The same embed between two workbooks already in memory (the open chain — sdmsWorkbookIo.ts):
 *  copies the calibration's sheet(s) from `cal` into `cur` and returns their names. */
export function embedCalibrationSheetsIntoWorkbook(
  cur: import('exceljs').Workbook,
  cal: import('exceljs').Workbook,
  model: number,
): string[] {
  const existing = new Set(cur.worksheets.map(w => w.name.toUpperCase()));
  const wanted = (name: string): boolean =>
    model === 2
      ? !SKIP_FOR_SET.has(name.toUpperCase())
      : name.toUpperCase() === 'CALIBRATION';

  const copied: string[] = [];
  for (const src of cal.worksheets) {
    if (!wanted(src.name)) continue;
    if (existing.has(src.name.toUpperCase())) {
      if (model === 2) {
        console.warn(`[SDMS] calibration sheet "${src.name}" already present on the batch — skipped`);
        continue;
      }
      // Single "Calibration" sheet: OG copies the calibration INTO the batch's own sheet
      // (`Worksheets["Calibration"].CopyFrom(...)`, Dc_AcceptingAttachCalibation :10560) — a template
      // that carries a Calibration sheet (Phosphate 8076, Ortho Phosphate 8101, Color 8084) merges it
      // header-only, and skipping it left the curve, Calibration ID and R² blank (2026-09-26). The
      // sheet keeps its place in the tab order.
      const old = cur.worksheets.find(w => w.name.toUpperCase() === src.name.toUpperCase())!;
      const orderNo = (old as unknown as { orderNo: number }).orderNo;
      cur.removeWorksheet(old.id);
      const dst = cur.addWorksheet(src.name);
      (dst as unknown as { orderNo: number }).orderNo = orderNo;
      copySheet(src, dst);
      dst.state = 'visible';
      copied.push(src.name);
      continue;
    }
    const dst = cur.addWorksheet(src.name);
    copySheet(src, dst);
    dst.state = 'visible';
    existing.add(src.name.toUpperCase());
    copied.push(src.name);
  }
  return copied;
}
